import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';

interface Props {
  open: boolean;
  title: string;
  /** Sub-heading under the title. Use it for the rule the form is subject to. */
  description?: string;
  onClose: () => void;
  children: ReactNode;
  /** Buttons. Rendered right-aligned in the footer, outside the scrolling body. */
  footer?: ReactNode;
  /** Set when the dialog wraps a form, so Enter submits and the footer button can be its
   *  submit control. */
  onSubmit?: (e: React.FormEvent) => void;
  /**
   * `wide` for dialogs that compose several things rather than filling a few fields — the
   * form builder, where a narrow column would put one question per screenful.
   */
  size?: 'default' | 'wide';
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal dialog for master-data entry.
 *
 * Rendered through a portal on `document.body` rather than in place: a dialog nested
 * inside `.card` would inherit that card's `overflow-x: auto` and clip itself.
 *
 * Three things a `<div>` with a shadow does not give you, and which a keyboard user
 * needs: Escape closes, Tab cycles inside the dialog instead of wandering into the page
 * behind it, and focus returns to whatever opened the dialog when it closes.
 */
export function Modal({
  open, title, description, onClose, children, footer, onSubmit, size = 'default',
}: Props) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement | null>(null);
  /** What had focus before the dialog opened, so it can be given back. */
  const restoreTo = useRef<HTMLElement | null>(null);

  /**
   * `onClose` is nearly always an inline arrow, so its identity changes on every render of
   * the parent. Held in a ref and kept OUT of the effect's dependencies: if the effect
   * re-ran per render it would re-run its focus setup too, and every keystroke in the form
   * would throw focus back to the first field.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    restoreTo.current = document.activeElement as HTMLElement | null;
    document.documentElement.classList.add('modal-open');

    // The first FIELD, so typing can start immediately — searching the body before the
    // whole panel, because the close button in the header would otherwise win. Falls back
    // to the panel itself when the dialog has nothing focusable.
    const body = panel.current?.querySelector('.modal-body');
    const first = body?.querySelector<HTMLElement>(FOCUSABLE)
      ?? panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;

      const stops = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.offsetParent !== null);
      if (stops.length === 0) return;

      const first = stops[0]!;
      const last = stops[stops.length - 1]!;
      const active = document.activeElement;

      // Wrap at both ends, and pull focus back in if it has escaped the panel entirely.
      if (e.shiftKey && (active === first || !panel.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.documentElement.classList.remove('modal-open');
      restoreTo.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const body = (
    <>
      <div className="modal-head">
        <div>
          <h3 id={titleId}>{title}</h3>
          {description && <p className="modal-desc">{description}</p>}
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Tutup">
          <Icon name="close" size={18} />
        </button>
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-foot">{footer}</div>}
    </>
  );

  return createPortal(
    <div
      className="modal-backdrop"
      // A click that STARTED inside the panel and ended on the backdrop (a drag out of a
      // text selection) must not close the dialog, so this checks the target itself.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={size === 'wide' ? 'modal is-wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panel}
        tabIndex={-1}
      >
        {onSubmit ? <form onSubmit={onSubmit} className="modal-form">{body}</form> : body}
      </div>
    </div>,
    document.body,
  );
}
